import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import FlexxLogoImg from "./FlexxLogoImg";
import PushNotifications from "./PushNotifications";
import SessionsClock from "../flexx/SessionsClock";

const API = process.env.REACT_APP_BACKEND_URL;

// Brand tokens — consistent with rest of app
const T = {
  bg: "#060606",
  surface: "#0C0C0C",
  panel: "#111111",
  border: "#1C1C1C",
  borderHover: "#2A2A2A",
  gold: "#C9A227",
  goldLight: "#E8B94F",
  goldGlow: "rgba(201,162,39,0.12)",
  text: "#EDE8DC",
  mute: "#AAAAAA",
  red: "#FF4444",
};

const AXEL_TIPS = [
  "Before every live session: complete your S — Scan. Come with setups, not questions about what to trade.",
  "Watching a live session is observation. Trading it in real-time requires your P — Plan written BEFORE the session starts.",
  "The replay library is not entertainment. Watch one replay per week and grade the trades using SuPER.",
  "Live sessions are hosted in PHT and shown in your local time. Set your alarm. Consistency compounds.",
];

export default function LiveRoom() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);

  const todayTip = AXEL_TIPS[new Date().getDate() % AXEL_TIPS.length];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = localStorage.getItem("flexx_access_token");
        const res = await axios.get(`${API}/api/live/state`, { headers: { Authorization: `Bearer ${token}` } });
        if (mounted) setState(res.data);
      } catch (e) {
        console.error("Failed to load live state", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return <div style={{ minHeight: "100vh", background: T.bg, color: T.mute, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", letterSpacing: 2 }}>LOADING LIVE ROOM…</div>;
  }
  if (!state) {
    return <div style={{ minHeight: "100vh", background: T.bg, color: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>Failed to load live state.</div>;
  }

  const updateState = async (patch) => {
    const token = localStorage.getItem("flexx_access_token");
    const res = await axios.put(`${API}/api/live/state`, patch, { headers: { Authorization: `Bearer ${token}` } });
    setState(res.data);
  };

  return (
    <div data-testid="live-room-page" style={{ minHeight: "100vh", background: T.bg, color: T.text, padding: "32px 20px 80px", fontFamily: "Inter, sans-serif" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <LiveStatusHeader state={state} isAdmin={isAdmin} onAdminToggle={() => setAdminPanelOpen((s) => !s)} />

        {isAdmin && adminPanelOpen && <AdminPanel state={state} onUpdate={updateState} />}

        <StreamPlayer state={state} onLibrary={() => navigate("/library")} />

        <SessionsClock />

        <AlertTicker alerts={state.alerts} />
        <div style={{ marginTop: 10 }}>
          <PushNotifications />
        </div>

        <HubLinkCard onClick={() => navigate("/library")} />

        <AxelLiveTip tip={todayTip} />
      </div>
    </div>
  );
}

// ── Live Status Header ──────────────────────────────────────────────
function LiveStatusHeader({ state, isAdmin, onAdminToggle }) {
  return (
    <header style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <FlexxLogoImg data-testid="live-header-logo" style={{ height: 44, width: "auto", filter: `drop-shadow(0 0 14px ${T.gold}55)`, userSelect: "none" }} />
        <div style={{ height: 40, width: 1, background: T.border }} />
        <div>
          <div style={{ fontSize: 10, letterSpacing: 3, color: T.gold, fontFamily: "monospace", marginBottom: 4 }}>ACADEMY</div>
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: -0.5, fontFamily: "'Bebas Neue', 'Oswald', sans-serif", color: T.text }}>LIVE ROOM</h1>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <StatusBadge isLive={state.is_live} />
        {isAdmin && (
          <button data-testid="live-admin-toggle-btn" onClick={onAdminToggle}
            style={{ padding: "8px 14px", borderRadius: 6, background: "transparent", border: `1px solid ${T.borderHover}`, color: T.gold, cursor: "pointer", fontSize: 11, fontFamily: "monospace", letterSpacing: 1.5 }}>
            ⚙ ADMIN
          </button>
        )}
      </div>
    </header>
  );
}

function StatusBadge({ isLive }) {
  return (
    <div data-testid="live-status-badge" style={{
      display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 6,
      background: isLive ? "rgba(255,68,68,0.1)" : T.surface, border: `1px solid ${isLive ? T.red + "60" : T.border}`,
      fontFamily: "monospace", fontSize: 12, letterSpacing: 1.5,
    }}>
      <span data-testid={isLive ? "live-dot-on" : "live-dot-off"} className={isLive ? "flexx-pulse-red" : ""}
        style={{ width: 10, height: 10, borderRadius: "50%", background: isLive ? T.red : T.mute, display: "inline-block" }} />
      <span style={{ color: isLive ? T.red : T.mute, fontWeight: 700 }}>{isLive ? "● LIVE NOW" : "○ OFFLINE"}</span>
    </div>
  );
}

// ── Admin Panel (stream + alerts only; schedule & videos live in Academy Hub) ──
function AdminPanel({ state, onUpdate }) {
  const [tab, setTab] = useState("stream");
  return (
    <div data-testid="live-admin-panel" style={card({ borderColor: T.gold + "60", background: T.goldGlow, marginBottom: 18 })}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: T.gold, fontFamily: "monospace" }}>ADMIN — LIVE ROOM CONTROLS</div>
        <div data-testid="live-admin-tabs" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[{ k: "stream", label: "STREAM" }, { k: "alerts", label: "ALERTS" }].map((t) => (
            <button key={t.k} data-testid={`live-admin-tab-${t.k}`} onClick={() => setTab(t.k)} style={btn({ active: tab === t.k, color: T.gold })}>{t.label}</button>
          ))}
        </div>
      </div>
      {tab === "stream" && <AdminStreamTab state={state} onUpdate={onUpdate} />}
      {tab === "alerts" && <AdminAlertsEditor state={state} onUpdate={onUpdate} />}
    </div>
  );
}

function AdminStreamTab({ state, onUpdate }) {
  const [streamUrl, setStreamUrl] = useState(state.stream_url || "");
  const [zoomUrl, setZoomUrl] = useState(state.zoom_url || "");
  const DEFAULT_AUTO_MSG = "🔴 We're live now — join the room.";
  const [autoPushOn, setAutoPushOn] = useState(() => {
    try { return JSON.parse(localStorage.getItem("flexx_live_autopush_on") || "true"); } catch (_) { return true; }
  });
  const [autoPushMsg, setAutoPushMsg] = useState(() => localStorage.getItem("flexx_live_autopush_msg") || DEFAULT_AUTO_MSG);

  useEffect(() => { localStorage.setItem("flexx_live_autopush_on", JSON.stringify(autoPushOn)); }, [autoPushOn]);
  useEffect(() => { localStorage.setItem("flexx_live_autopush_msg", autoPushMsg); }, [autoPushMsg]);

  const goLive = () => {
    if (state.is_live) return;
    const patch = { is_live: true };
    if (autoPushOn && autoPushMsg.trim()) patch.alerts = [autoPushMsg.trim(), ...(state.alerts || [])];
    onUpdate(patch);
  };
  const goOffline = () => { if (state.is_live) onUpdate({ is_live: false }); };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
        <FieldGroup label="LIVE Status">
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="live-toggle-on-btn" onClick={goLive} style={btn({ active: state.is_live, color: T.red })}>ON</button>
            <button data-testid="live-toggle-off-btn" onClick={goOffline} style={btn({ active: !state.is_live, color: T.mute })}>OFF</button>
          </div>
        </FieldGroup>
        <FieldGroup label="YouTube Stream URL or Video ID">
          <div style={{ display: "flex", gap: 8 }}>
            <input data-testid="live-stream-url-input" value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)} placeholder="dQw4w9WgXcQ or full URL" style={input} />
            <button data-testid="live-stream-save-btn" onClick={() => onUpdate({ stream_url: streamUrl.trim() })} style={btn({ active: true, color: T.gold })}>Save</button>
          </div>
          <div style={muteHint}>Leave blank to hide the YouTube join button.</div>
        </FieldGroup>
        <FieldGroup label="Zoom URL">
          <div style={{ display: "flex", gap: 8 }}>
            <input data-testid="live-zoom-url-input" value={zoomUrl} onChange={(e) => setZoomUrl(e.target.value)} placeholder="https://zoom.us/j/..." style={input} />
            <button data-testid="live-zoom-save-btn" onClick={() => onUpdate({ zoom_url: zoomUrl.trim() })} style={btn({ active: true, color: T.gold })}>Save</button>
          </div>
          <div style={muteHint}>Leave blank to hide the Zoom join button.</div>
        </FieldGroup>
      </div>

      <div style={{ padding: 12, background: T.panel, border: `1px dashed ${T.gold}55`, borderRadius: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
          <input data-testid="live-autopush-toggle" type="checkbox" checked={autoPushOn} onChange={(e) => setAutoPushOn(e.target.checked)} style={{ width: 16, height: 16, accentColor: T.gold, cursor: "pointer" }} />
          <span style={{ fontSize: 11, fontFamily: "monospace", letterSpacing: 1.5, color: autoPushOn ? T.gold : T.mute, fontWeight: 700 }}>AUTO-PUSH ALERT WHEN LIVE TURNS ON</span>
        </label>
        <div style={{ marginTop: 10, display: "flex", gap: 8, opacity: autoPushOn ? 1 : 0.5 }}>
          <input data-testid="live-autopush-msg-input" value={autoPushMsg} onChange={(e) => setAutoPushMsg(e.target.value)} disabled={!autoPushOn} placeholder={DEFAULT_AUTO_MSG} maxLength={240} style={input} />
          <button data-testid="live-autopush-reset-btn" onClick={() => setAutoPushMsg(DEFAULT_AUTO_MSG)} disabled={!autoPushOn} style={btn({ active: false, color: T.mute })}>Reset</button>
        </div>
        <div style={muteHint}>When you flip LIVE → ON, this message is prepended to the scrolling ticker automatically.</div>
      </div>
    </div>
  );
}

function AdminAlertsEditor({ state, onUpdate }) {
  const [rows, setRows] = useState(state.alerts || []);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const updateRow = (i, value) => setRows((r) => r.map((x, idx) => (idx === i ? value : x)));
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const reset = () => setRows(state.alerts || []);

  const sendTestPush = async () => {
    setTestBusy(true); setTestResult(null);
    try {
      const token = localStorage.getItem("flexx_access_token");
      const res = await axios.post(`${API}/api/admin/push/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTestResult(`Sent: ${res.data.sent}, pruned: ${res.data.pruned}`);
    } catch (e) {
      setTestResult("Test push failed: " + (e.response?.data?.detail || e.message));
    } finally { setTestBusy(false); }
  };

  const saveAll = async () => {
    setBusy(true);
    try {
      const cleaned = rows.map((s) => (s || "").trim()).filter(Boolean);
      await onUpdate({ alerts: cleaned });
      setRows(cleaned);
    } finally { setBusy(false); }
  };

  const pushNow = async () => {
    const msg = draft.trim();
    if (!msg) return;
    setPushBusy(true);
    try {
      const next = [msg, ...rows.map((s) => (s || "").trim()).filter(Boolean)];
      await onUpdate({ alerts: next });
      setRows(next);
      setDraft("");
    } finally { setPushBusy(false); }
  };

  const onPushKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); pushNow(); } };

  return (
    <div data-testid="live-admin-alerts-editor">
      <div style={muteHint}>Push a one-line announcement to the scrolling ticker. Pushes also fan out as OS-level notifications to subscribed users.</div>
      <div style={{ display: "flex", gap: 8, marginTop: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <input data-testid="live-admin-alert-push-input" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onPushKey} placeholder="📢 Session starts in 15 min — be at your desk." style={{ ...input, flex: 1, minWidth: 220 }} maxLength={240} />
        <button data-testid="live-admin-alert-push-btn" onClick={pushNow} disabled={pushBusy || !draft.trim()} style={btn({ active: !!draft.trim(), color: T.gold })}>{pushBusy ? "Pushing…" : "🔔 Push"}</button>
        <button data-testid="live-admin-test-push-btn" onClick={sendTestPush} disabled={testBusy} title="Send a test OS notification to every subscribed user" style={btn({ active: false, color: T.mute })}>{testBusy ? "…" : "🧪 Test Push"}</button>
      </div>
      {testResult && <div style={{ marginTop: -6, marginBottom: 10, color: T.mute, fontFamily: "monospace", fontSize: 11 }}>{testResult}</div>}

      <div style={{ fontSize: 9, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 8 }}>CURRENT TICKER ({rows.length})</div>
      <div style={{ display: "grid", gap: 8, maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
        {rows.length === 0 && <div style={{ color: T.mute, padding: 14, textAlign: "center", fontSize: 12 }}>No alerts. Push one above to start broadcasting.</div>}
        {rows.map((row, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 28px", gap: 8, alignItems: "center", padding: 10, background: T.panel, border: `1px solid ${T.border}`, borderRadius: 8 }}>
            <input data-testid={`live-admin-alert-input-${i}`} value={row || ""} onChange={(e) => updateRow(i, e.target.value)} style={input} maxLength={240} />
            <button data-testid={`live-admin-alert-delete-${i}`} onClick={() => removeRow(i)} title="Remove alert" style={{ width: 28, height: 28, borderRadius: 6, background: "transparent", border: `1px solid ${T.border}`, color: T.red, cursor: "pointer", fontSize: 14, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
        <button data-testid="live-admin-alert-reset-btn" onClick={reset} style={btn({ active: false, color: T.mute })}>Reset</button>
        <button data-testid="live-admin-alert-save-btn" onClick={saveAll} disabled={busy} style={btn({ active: true, color: T.gold })}>{busy ? "Saving…" : "Save All"}</button>
      </div>
    </div>
  );
}

// ── Stream Player ───────────────────────────────────────────────────
function StreamPlayer({ state, onLibrary }) {
  const videoId = extractYouTubeId(state.stream_url);
  const hasZoom = !!(state.zoom_url && state.zoom_url.trim());
  const hasYoutube = !!videoId;
  const iframeRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const unmute = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage('{"event":"command","func":"unMute","args":""}', "*");
    win.postMessage('{"event":"command","func":"playVideo","args":""}', "*");
    setMuted(false);
  };

  const openZoom = () => {
    if (!state.zoom_url) return;
    const w = Math.min(1280, window.innerWidth - 100);
    const h = Math.min(800, window.innerHeight - 80);
    const left = Math.max(0, (window.screen.width - w) / 2);
    const top = Math.max(0, (window.screen.height - h) / 2);
    const popup = window.open(state.zoom_url, "flexx-zoom", `popup=yes,width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) window.open(state.zoom_url, "_blank", "noopener,noreferrer");
  };

  const joinTargets = [];
  if (hasZoom) joinTargets.push({ key: "zoom", label: "🎙 JOIN ZOOM (popup)", onClick: openZoom, primary: true });
  if (hasYoutube) joinTargets.push({ key: "youtube", label: "▶ OPEN YOUTUBE TAB", onClick: () => window.open(`https://www.youtube.com/watch?v=${videoId}`, "_blank"), primary: !hasZoom });

  return (
    <section style={{ marginBottom: 24 }}>
      <div data-testid="live-stream-row" style={{ display: "grid", gridTemplateColumns: hasYoutube ? "minmax(0,1fr) 320px" : "1fr", gap: 12, alignItems: "stretch" }} className="flexx-live-stream-row">
        <div style={{ position: "relative" }}>
          <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, background: T.surface, border: `1px solid ${state.is_live ? T.gold : T.border}`, borderRadius: 12, overflow: "hidden", boxShadow: state.is_live ? `0 0 40px ${T.goldGlow}` : "none" }}>
            {videoId ? (
              <iframe ref={iframeRef} data-testid="live-stream-iframe" src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&enablejsapi=1&rel=0`} title="FLEXX Live Stream" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
            ) : (
              <OfflinePlaceholder />
            )}
          </div>
          {hasYoutube && muted && (
            <button data-testid="live-stream-unmute-btn" onClick={unmute} style={{ position: "absolute", top: 14, right: 14, zIndex: 5, padding: "10px 16px", borderRadius: 999, background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`, color: "#0a0a0a", border: "none", cursor: "pointer", fontFamily: "monospace", fontSize: 12, fontWeight: 800, letterSpacing: 1.5, boxShadow: `0 0 22px rgba(201,162,39,0.55)`, animation: "flexx-pulse-gold 1.4s ease-in-out infinite" }}>🔊 TAP FOR SOUND</button>
          )}
        </div>
        {hasYoutube && <LiveChatEmbed videoId={videoId} />}
      </div>

      <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
        {joinTargets.length > 0 && (
          <div data-testid="live-join-targets" style={{ display: "grid", gridTemplateColumns: joinTargets.length === 2 ? "1fr 1fr" : "1fr", gap: 10 }}>
            {joinTargets.map((t) => (
              <button key={t.key} data-testid={`live-join-${t.key}-btn`} onClick={t.onClick} style={ctaBtn(t.primary)}>{t.label}</button>
            ))}
          </div>
        )}
        <button data-testid="live-view-library-btn" onClick={onLibrary} style={ctaBtn(joinTargets.length === 0)}>📚 OPEN ACADEMY HUB</button>
      </div>

      <style>{`@media (max-width: 900px) { .flexx-live-stream-row { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function LiveChatEmbed({ videoId }) {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  if (!host) return null;
  return (
    <div data-testid="live-stream-chat" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 320 }}>
      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}`, fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, color: T.gold, fontWeight: 700 }}>💬 LIVE CHAT</div>
      <iframe data-testid="live-stream-chat-iframe" title="FLEXX Live Chat" src={`https://www.youtube.com/live_chat?v=${videoId}&embed_domain=${host}&dark_theme=1`} style={{ flex: 1, width: "100%", border: 0, minHeight: 320 }} />
    </div>
  );
}

function OfflinePlaceholder() {
  return (
    <div data-testid="live-offline-placeholder" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, background: `radial-gradient(ellipse at center, ${T.goldGlow} 0%, ${T.bg} 70%)` }}>
      <FlexxLogoImg data-testid="live-offline-logo" style={{ width: "min(420px, 70%)", height: "auto", filter: `drop-shadow(0 0 30px ${T.gold}55) drop-shadow(0 0 60px ${T.gold}22)`, userSelect: "none", pointerEvents: "none" }} />
      <div style={{ fontSize: 12, color: T.mute, fontFamily: "monospace", letterSpacing: 2 }}>NEXT SESSION STARTS SOON</div>
    </div>
  );
}

// ── Alert Ticker ────────────────────────────────────────────────────
function AlertTicker({ alerts }) {
  const text = (alerts || []).join("   •   ");
  return (
    <div data-testid="live-alert-ticker" style={{ background: "linear-gradient(90deg, #0d0a04 0%, #15110a 50%, #0d0a04 100%)", border: `1px solid ${T.gold}40`, borderRadius: 8, padding: "12px 0", marginBottom: 24, overflow: "hidden", whiteSpace: "nowrap", position: "relative" }}>
      <div style={{ display: "inline-block", paddingLeft: "100%", animation: "flexx-ticker-scroll 60s linear infinite", color: T.gold, fontFamily: "monospace", fontSize: 13, letterSpacing: 0.5 }}>
        {text}   •   {text}
      </div>
      <style>{`@keyframes flexx-ticker-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-100%); } }`}</style>
    </div>
  );
}

// ── Academy Hub link card ───────────────────────────────────────────
function HubLinkCard({ onClick }) {
  return (
    <button data-testid="live-hub-link-card" onClick={onClick} style={{
      width: "100%", textAlign: "left", cursor: "pointer", marginBottom: 24,
      ...card({ borderColor: T.gold + "40", borderLeft: `3px solid ${T.gold}`, background: "linear-gradient(135deg, #0d0a04 0%, #12100a 100%)" }),
      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
    }}>
      <div>
        <div style={{ fontSize: 9, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 4 }}>📚 ACADEMY HUB</div>
        <div style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>Training Schedule + Recorded Sessions Library</div>
        <div style={{ color: T.mute, fontSize: 12, marginTop: 3 }}>Browse Must-Watch & Beginner playlists, replays and the weekly plan.</div>
      </div>
      <span style={{ color: T.gold, fontSize: 20 }}>→</span>
    </button>
  );
}

// ── AXEL Live Tip ───────────────────────────────────────────────────
function AxelLiveTip({ tip }) {
  return (
    <section data-testid="live-axel-tip" style={card({ borderColor: T.gold + "60", borderLeft: `3px solid ${T.gold}`, background: "linear-gradient(135deg, #0d0a04 0%, #15110a 100%)", boxShadow: `0 0 24px ${T.goldGlow}`, padding: 18 })}>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{ width: 44, height: 44, borderRadius: 22, flexShrink: 0, background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#0a0a0a", fontWeight: 800, fontFamily: "monospace", fontSize: 14 }}>AX</div>
        <div>
          <div style={{ fontSize: 9, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 4 }}>AXEL — LIVE TIP</div>
          <div style={{ color: T.text, fontSize: 14, lineHeight: 1.6 }}>{tip}</div>
        </div>
      </div>
    </section>
  );
}

// ── helpers ─────────────────────────────────────────────────────────
function extractYouTubeId(inputVal) {
  if (!inputVal) return "";
  const s = inputVal.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  const m = s.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : "";
}

// ── style helpers ───────────────────────────────────────────────────
const card = (opts = {}) => ({
  background: opts.background || T.surface,
  border: `1px solid ${opts.borderColor || T.border}`,
  borderLeft: opts.borderLeft || `1px solid ${opts.borderColor || T.border}`,
  borderRadius: 10,
  padding: opts.padding ?? 16,
  boxShadow: opts.boxShadow,
  overflow: opts.overflow,
  marginBottom: opts.marginBottom,
});
const ctaBtn = (primary) => ({
  padding: "14px 20px", minHeight: 44, borderRadius: 8,
  background: primary ? `linear-gradient(135deg, ${T.gold}, ${T.goldLight})` : "transparent",
  border: primary ? "none" : `1px solid ${T.gold}`,
  color: primary ? "#0a0a0a" : T.gold,
  fontFamily: "monospace", fontSize: 12, fontWeight: 800, letterSpacing: 2, cursor: "pointer", textTransform: "uppercase",
});
const input = {
  flex: 1, padding: "8px 10px", borderRadius: 6, background: T.panel, border: `1px solid ${T.border}`,
  color: T.text, fontSize: 13, outline: "none",
};
const btn = ({ active, color }) => ({
  padding: "8px 14px", borderRadius: 6, background: active ? color + "22" : "transparent",
  border: `1px solid ${active ? color : T.border}`, color: active ? color : T.mute,
  cursor: "pointer", fontSize: 11, fontFamily: "monospace", letterSpacing: 1, fontWeight: 700,
});
const muteHint = { fontSize: 10, color: T.mute, marginTop: 6, fontFamily: "monospace", letterSpacing: 0.5 };

function FieldGroup({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 9, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}
